import { useEffect } from 'react';
import { createFileRoute, redirect, Outlet, useNavigate } from '@tanstack/react-router';
// Layout route — NOT code-split. Direct source import to avoid barrel cascade.
import type { AuthState } from '@amber-ui/core/src/contexts/auth-context';
import { useAuth } from '@amber-ui/core/src/contexts/auth-context';
import { AuthLayout } from '../component/AuthLayout';
import { AuthPollingProvider } from '@amber-ui/core/src/component/util/AuthPollingProvider';

/**
 * Error component for the _auth layout route. Catches FORBIDDEN errors that may
 * arise from an invalid `acct` parameter referencing an unauthorized account.
 * Strips the invalid acct (and bin) params and redirects to the default dashboard.
 */
function AuthErrorComponent({ error }: { error: Error; reset: () => void }) {
  const navigate = useNavigate();

  const isForbidden =
    error.message?.includes('FORBIDDEN') || error.message?.includes('unauthorized');

  useEffect(() => {
    if (isForbidden) {
      void navigate({ to: '/grain_bin/dashboard', search: {}, replace: true });
    }
  }, [isForbidden, navigate]);

  if (isForbidden) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>Redirecting to dashboard...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, textAlign: 'center' }}>
      <h2>Something went wrong</h2>
      <p>{error.message}</p>
      <a href="/grain_bin/dashboard">Go to Dashboard</a>
    </div>
  );
}

/**
 * Auth layout route — guards authentication and wraps all authenticated
 * content in the app shell (sidebar navigation, top bar, alerts, etc.).
 *
 * The `beforeLoad` hook reads from AuthContext (injected into router context
 * by AppInner) and redirects unauthenticated users to /login.
 * The `component` renders AuthLayout which provides the full app chrome,
 * with <Outlet /> for child route content.
 */
export const Route = createFileRoute('/_auth')({
  // Validate the `acct` search parameter (Sqid-encoded account ID).
  // Valid: non-empty alphanumeric string matching Sqid charset [a-zA-Z0-9].
  // Invalid values (special chars, empty) are stripped to undefined.
  // This validated type { acct?: string } is inherited by all child routes
  // via TanStack Router's type inference — no manual wiring needed.
  validateSearch: (search: Record<string, unknown>): { acct?: string } => ({
    acct:
      typeof search.acct === 'string' && /^[a-zA-Z0-9]{8,}$/.test(search.acct)
        ? search.acct
        : undefined,
  }),
  beforeLoad: ({ context: { auth }, location }) => {
    // auth is undefined on the very first route resolution (before React renders
    // AppInner which injects auth into router context). The guard passes through
    // silently; the route's pendingComponent handles the loading state until
    // AuthProvider signals ready=true and AppInner re-renders with the auth value.
    if (auth) {
      guardWithAuthContext(auth, location.pathname);
    }
  },
  errorComponent: AuthErrorComponent,
  component: function AuthLayoutRoute() {
    // Read session state reactively from AuthContext for the polling provider.
    const { user_id, account_id } = useAuth();

    return (
      <>
        <AuthPollingProvider user_id={user_id} account_id={account_id} />
        <AuthLayout>
          <Outlet />
        </AuthLayout>
      </>
    );
  },
});

/**
 * Auth guard logic using AuthContext state:
 * - If not ready: return without redirecting (pending UI shows while auth initializes)
 * - If ready and no account_id: redirect to /login
 * - If email unverified and not already navigating to /user/*: redirect to /user/email_verification
 */
function guardWithAuthContext(auth: AuthState, targetPathname: string): void {
  // Auth not yet initialized — let the pending UI handle it.
  // AuthProvider sets ready=true after token renewal completes (or fails).
  if (!auth.ready) return;

  // Authenticated but no account — redirect to login.
  // We check BOTH account_id AND email_address because there's a brief
  // transition window after login where the session has set email_address
  // (from the createToken mutation response) but account_id is momentarily null
  // before the login() call completes.
  if (auth.account_id === null && !auth.email_address) {
    throw redirect({ to: '/login' });
  }

  // Redirect unverified email users to the verification page.
  // The backend does not enforce email verification at the API level — this is
  // client-side-only UX enforcement to ensure users complete registration.
  // Guard against infinite redirect: don't redirect if the target is a /user/ path
  // (the email_verification route IS under _auth at /_auth/user/email_verification).
  if (!auth.verified_email && !targetPathname.startsWith('/user')) {
    throw redirect({ to: '/user/email_verification' });
  }
}
